import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { generateClient } from "aws-amplify/api";
import { GQL_QUERIES } from "../queries";
import { resultsTransformer } from "../util/queryResultsTransformers";
import { GqlErrorTransformer } from "../util/GqlErrorTransformer";
import { parseApplicationData } from '../util/queryResultsTransformers';

const client = generateClient();

export const graphqlApi = createApi({
    reducerPath: "graphqlApi",
    baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
    tagTypes: ["StudentDashboardApplicationStatus"],
    endpoints: (builder) => ({
        // StudentDashboard - Student Application Status
        getStudentDashboardApplicationStatus: builder.query({
            queryFn: async () => {
                try {
                    const result = await getDataByQueryName("getSelfService");

                    if (!result.data) {
                        throw new Error('No application data found for user');
                    }

                    // Parse the AWSJson fields (honors and scholars are JSON strings)
                    const parsedData = parseApplicationData(result.data);

                    return { data: parsedData };
                } catch (error) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            error: error.message || 'Failed to fetch application data'
                        }
                    };
                }
            },
            providesTags: ['StudentDashboardApplicationStatus'],
        }),

        submitScholarProgramPreferences: builder.mutation({
            queryFn: async ({ emplid, preferences }) => {
                try {
                    // TODO: Add your preferences submission mutation here following the same pattern
                    // const result = await getDataByQueryName("submitPreferences", { emplid, preferences });
                    return {
                        data: { success: true, message: 'Preferences submitted successfully' }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'SUBMISSION_ERROR',
                            error: error.message || 'Failed to submit preferences'
                        }
                    };
                }
            },
            invalidatesTags: ['StudentDashboardApplicationStatus'],
        }),

        // Admin Dashboard - Student Application Status (View Only)
        getStudentDashboardApplicationStatusViewOnly: builder.query({
            queryFn: async (emplid) => {
                try {
                    const result = await getDataByQueryName("getSelfServiceViewOnly", { emplid: emplid });

                    if (!result.data) {
                        throw new Error('No application data found for student');
                    }

                    // Parse the AWSJson fields (honors and scholars are JSON strings)
                    const parsedData = parseApplicationData(result.data);

                    return { data: parsedData };
                } catch (error) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            error: error.message || 'Failed to fetch student application data'
                        }
                    };
                }
            },
            providesTags: (result, error, emplid) => [
                { type: 'StudentDashboardApplicationStatus', id: `admin-${emplid}` }
            ],
        }),

        // Admin Dashboard - Person Search
        personSearch: builder.query({
            queryFn: async (searchTerm) => {
                try {
                    const result = await getDataByQueryName("personSearch", { input: { searchTerm } });

                    if (!result.data) {
                        return { data: { columns: [], results: [] } };
                    }

                    // Parse the results JSON string if it exists
                    let parsedResults = [];
                    if (result.data.results) {
                        try {
                            parsedResults = JSON.parse(result.data.results);
                        } catch (parseError) {
                            console.warn('Failed to parse search results:', parseError);
                            parsedResults = [];
                        }
                    }

                    // Filter to only the fields we need and handle missing email
                    const filteredResults = parsedResults.map(person => ({
                        applicantEmployeeId: person.applicantEmployeeId || '',
                        firstName: person.firstName || '',
                        lastName: person.lastName || '',
                        email: person.campusEmail || person.admissionsEmail ||  null, // Can be null
                        programId: person.programId || '',
                        term: person.term || ''
                    }));

                    // Filter columns to only show the ones we need
                    const neededFields = ['applicantEmployeeId', 'firstName', 'lastName', 'email', 'programId', 'term'];
                    const filteredColumns = result.data.columns.filter(col =>
                        neededFields.includes(col.field)
                    );

                    return {
                        data: {
                            columns: filteredColumns,
                            results: filteredResults
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            error: error.message || 'Failed to search for persons'
                        }
                    };
                }
            },
        }),

        // Scholar Preferences Submission - Mock Implementation
        submitScholarPreferences: builder.mutation({
            queryFn: async (preferences) => {
                try {


                    // Simulate API delay
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    // Return mock success response
                    return {
                        data: {
                            success: true,
                            submissionId: `mock-${Date.now()}`,
                            message: 'Preferences submitted successfully'
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'SUBMISSION_ERROR',
                            error: error.message || 'Failed to submit scholar preferences'
                        }
                    };
                }
            },
            invalidatesTags: ['StudentDashboardApplicationStatus'],
        }),

        // Student Decision Submission (Decline Honors)
        submitStudentDecision: builder.mutation({
            queryFn: async ({ decision }) => {
                try {
                    const result = await getDataByQueryName("submitStudentDecision", {
                        decision: decision
                    });
                    return { data: result.data };
                } catch (error) {
                    return {
                        error: {
                            status: 'SUBMISSION_ERROR',
                            error: error.message || 'Failed to submit student decision'
                        }
                    };
                }
            },
            invalidatesTags: ['StudentDashboardApplicationStatus'],
        }),

        /* 
        // Future Real GraphQL Implementation - Ready for Drop-in
        submitScholarPreferences: builder.mutation({
            queryFn: async (preferences) => {
                try {
                    const result = await getDataByQueryName("submitScholarPreferences", {
                        input: {
                            emplid: preferences.emplid || 'current-user-emplid',
                            topThreeChoices: preferences.topThree || [],
                            programRatings: preferences.ratings || {},
                            submissionTimestamp: new Date().toISOString()
                        }
                    });
                    
                    return { data: result.data };
                } catch (error) {
                    return {
                        error: {
                            status: 'SUBMISSION_ERROR',
                            error: error.message || 'Failed to submit scholar preferences'
                        }
                    };
                }
            },
            invalidatesTags: ['StudentDashboardApplicationStatus'],
        }),
        */

    }),
});

// RTKQ auto-unwraps from data, still need to unwrap inner queryname wrapper though
async function getDataByQueryName(queryName, variables) {
    if (!GQL_QUERIES[queryName]) { throw `queryName '${queryName}' not found, please check imports`; } // eslint-disable-line
    const queryParameters = { query: GQL_QUERIES[queryName], variables };

    const apiCallAsFn = async () => (await client.graphql(queryParameters)).data[queryName];
    const dataFromApi = await GqlErrorTransformer(apiCallAsFn); // Execute API call, and capture+throw transformed error results
    const dataTransformed = resultsTransformer(dataFromApi, queryName);
    return { data: dataTransformed };
}

export const {
    // StudentDashboard
    useGetStudentDashboardApplicationStatusQuery,
    useSubmitScholarProgramPreferencesMutation,
    // Admin Dashboard
    useGetStudentDashboardApplicationStatusViewOnlyQuery,
    usePersonSearchQuery,
    // Scholar Preferences
    useSubmitScholarPreferencesMutation,
    // Student Decisions
    useSubmitStudentDecisionMutation,
} = graphqlApi;

// export const useGetApplicationDetailsQueryState = graphqlApi.endpoints.getApplicationDetails.useQueryState;